import { useState } from 'react';
import ReactDOM from 'react-dom';

import AuthClient from '@skyscanner/auth-client';
import {
  fetchCultureSelectorData,
  getSessionHandoffToken,
  useCultureSelectorData,
  persistUserSessionData,
} from '@skyscanner-internal/global-components/culture-selector/helpers';
import Header from '@skyscanner-internal/global-components/header';
import { SubscriptionClient } from '@skyscanner-internal/global-components/login-form/helpers';
import { GLOBAL } from 'saddlebag-browser';
import { logError, logOperationalEvent, logWarn } from 'saddlebag-logger';
import { isLoggedIn, getProfilePicture } from 'saddlebag-user-management';

import type { LoginFormEmbeddedProps } from '@skyscanner-internal/global-components/header/types';
import type { Logger } from '@skyscanner-internal/global-components/types/utils';

export const GlobalHeader = () => {
  const $window = GLOBAL.getWindow();
  const { headerComponentData } = $window.__internal;
  const rootContainer = document.getElementById('global-header');

  const [isLoginFormOpen, setIsLoginFormOpen] = useState(false);

  const logger: Logger = {
    logError,
    logWarn,
    logOperationalEvent,
  };
  const {
    currencies,
    enMarkets,
    isFetchingCultureSelectorData,
    locales,
    markets,
    onCultureSelectorButtonClick,
  } = useCultureSelectorData(
    fetchCultureSelectorData,
    headerComponentData.locale,
    false,
  );

  let loginFormProps: LoginFormEmbeddedProps | undefined;

  if (headerComponentData.cultureSelectorProps) {
    headerComponentData.cultureSelectorProps.currencies = currencies;
    headerComponentData.cultureSelectorProps.markets = markets;
    headerComponentData.cultureSelectorProps.locales = locales;
    headerComponentData.cultureSelectorProps.enMarkets = enMarkets;
    headerComponentData.cultureSelectorProps.persistUserSessionDataFn =
      persistUserSessionData;
    headerComponentData.cultureSelectorProps.getSessionHandoffTokenFn =
      getSessionHandoffToken;
  }

  if (!rootContainer) return null;

  if (headerComponentData.loginFormProps) {
    const authClient = new AuthClient({
      authConsumer: 'month-view',
      env: process.env.NODE_ENV,
      locale: headerComponentData.locale,
      market: headerComponentData.market,
      strings: headerComponentData.loginFormProps.authClient.strings,
    });

    const subscriptionClient = new SubscriptionClient();

    loginFormProps = {
      ...headerComponentData.loginFormProps,
      authClient,
      subscriptionClient,
      isOpen: isLoginFormOpen,
      onVisibilityChange: ({ shouldBeOpen }: { shouldBeOpen?: boolean }) => {
        setIsLoginFormOpen(!!shouldBeOpen);
      },
    };
  }

  return ReactDOM.createPortal(
    <Header
      {...headerComponentData}
      onCultureSelectorButtonClick={onCultureSelectorButtonClick}
      isFetchingCultureSelectorData={isFetchingCultureSelectorData}
      logger={logger}
      userLoggedIn={isLoggedIn()}
      getProfilePicture={getProfilePicture}
      loginFormProps={loginFormProps}
    />,
    rootContainer,
  );
};
